/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/components/ModernFormTypeSwitch.tsx
 * Release: v3.0.0 - Design ultra-moderno con glassmorphism avanzato e effetti premium
 * Data: 23/07/2025
 * Descrizione: Commutatore azienda/privato con design ultra-moderno, effetti glassmorphism avanzati e micro-interazioni premium
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, User, CheckCircle, AlertTriangle, Sparkles } from 'lucide-react';

export type FormType = 'azienda' | 'privato';

interface ModernFormTypeSwitchProps {
  currentType: FormType;
  onTypeChange: (type: FormType) => void;
  disabled?: boolean;
  className?: string;
  showConfirmation?: boolean;
}

export const ModernFormTypeSwitch: React.FC<ModernFormTypeSwitchProps> = ({
  currentType,
  onTypeChange,
  disabled = false,
  className = '',
  showConfirmation = true
}) => {
  const [showModal, setShowModal] = useState(false);
  const [pendingType, setPendingType] = useState<FormType | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleTypeChange = (newType: FormType) => {
    if (newType === currentType || disabled) return;
    
    setIsAnimating(true);
    setTimeout(() => {
      if (showConfirmation) {
        setPendingType(newType);
        setShowModal(true);
      } else {
        onTypeChange(newType);
      }
      setIsAnimating(false);
    }, 150);
  };

  const confirmChange = () => {
    if (pendingType) {
      onTypeChange(pendingType);
      setShowModal(false);
      setPendingType(null);
    }
  };

  const cancelChange = () => {
    setShowModal(false);
    setPendingType(null);
  };

  const getTypeConfig = (type: FormType) => {
    const configs = {
      azienda: {
        icon: Building2,
        label: 'Azienda',
        description: 'Dati aziendali',
        color: 'from-blue-600 via-purple-600 to-blue-500',
        glowColor: 'shadow-blue-500/25',
        bgColor: 'bg-gradient-to-br from-blue-500/10 to-purple-500/10',
        borderColor: 'border-blue-500/30',
        textColor: 'text-blue-200',
        activeTextColor: 'text-blue-200'
      },
      privato: {
        icon: User,
        label: 'Privato',
        description: 'Dati personali',
        color: 'from-purple-600 via-blue-600 to-purple-500',
        glowColor: 'shadow-purple-500/25',
        bgColor: 'bg-gradient-to-br from-purple-500/10 to-blue-500/10',
        borderColor: 'border-purple-500/30',
        textColor: 'text-purple-200',
        activeTextColor: 'text-purple-200'
      }
    };
    return configs[type];
  };

  const currentConfig = getTypeConfig(currentType);

  return (
    <>
      {/* Container principale */}
      <div className={`w-full max-w-md ${className}`}>
        {/* Card contenitore con design ultra-moderno */}
        <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 border border-slate-600/50 rounded-2xl p-6 shadow-2xl backdrop-blur-sm">
          {/* Header con titolo e descrizione */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Sparkles size={16} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">Tipo Cliente</h3>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              Seleziona se stai registrando un'azienda o un cliente privato
            </p>
          </div>
          
          {/* Switch moderno con effetti avanzati */}
          <div className="relative">
            {/* Background animato */}
            <div className="absolute inset-0 bg-gradient-to-r from-slate-700/50 to-slate-600/50 rounded-xl blur-sm"></div>
            
            {/* Container switch */}
            <div className="relative bg-slate-800/80 backdrop-blur-sm border border-slate-600/30 rounded-xl p-1">
              {/* Slider animato */}
              <motion.div 
                className={`
                  absolute top-1 bottom-1 w-[calc(50%-2px)] bg-gradient-to-r ${currentConfig.color}
                  rounded-lg shadow-lg
                  ${isAnimating ? 'scale-105' : 'scale-100'}
                `}
                animate={{
                  x: currentType === 'azienda' ? 0 : '100%'
                }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 30 
                }}
              >
                {/* Effetto glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-lg animate-pulse"></div>
              </motion.div>
              
              {/* Pulsanti */}
              <div className="relative grid grid-cols-2 gap-1">
                {/* Pulsante Azienda */}
                <button
                  type="button"
                  onClick={() => handleTypeChange('azienda')}
                  disabled={disabled}
                  className={`
                    relative h-16 px-4 py-3 rounded-lg transition-all duration-300 ease-out
                    flex flex-col items-center justify-center gap-2 group
                    ${currentType === 'azienda'
                      ? 'text-white'
                      : 'text-slate-400 hover:text-slate-300'
                    }
                    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  aria-current={currentType === 'azienda' ? 'page' : undefined}
                >
                  {/* Icona con effetti */}
                  <motion.div 
                    className={`
                      relative transition-all duration-300
                      ${currentType === 'azienda' ? 'scale-110' : 'scale-100 group-hover:scale-105'}
                    `}
                    animate={{ 
                      rotate: currentType === 'azienda' ? [0, -10, 10, 0] : 0,
                      scale: currentType === 'azienda' ? 1.1 : 1
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <Building2 
                      size={24} 
                      className={`
                        transition-all duration-300
                        ${currentType === 'azienda' 
                          ? 'text-blue-200 drop-shadow-lg' 
                          : 'text-slate-400 group-hover:text-slate-300'
                        }
                      `} 
                    />
                    {/* Check mark per stato attivo */}
                    {currentType === 'azienda' && (
                      <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center animate-bounce"
                      >
                        <CheckCircle size={12} className="text-white" />
                      </motion.div>
                    )}
                  </motion.div>
                  
                  {/* Testo con animazioni */}
                  <div className="text-center">
                    <span className={`
                      font-bold text-sm transition-all duration-300
                      ${currentType === 'azienda' ? 'text-white' : 'text-slate-400 group-hover:text-slate-300'}
                    `}>
                      Azienda
                    </span>
                  </div>
                  
                  {/* Effetto hover */}
                  <div className={`
                    absolute inset-0 rounded-lg transition-all duration-300
                    ${currentType === 'azienda' 
                      ? 'bg-gradient-to-br from-blue-500/10 to-purple-500/10' 
                      : 'group-hover:bg-slate-700/30'
                    }
                  `}></div>
                </button>
                
                {/* Pulsante Privato */}
                <button
                  type="button"
                  onClick={() => handleTypeChange('privato')}
                  disabled={disabled}
                  className={`
                    relative h-16 px-4 py-3 rounded-lg transition-all duration-300 ease-out
                    flex flex-col items-center justify-center gap-2 group
                    ${currentType === 'privato'
                      ? 'text-white'
                      : 'text-slate-400 hover:text-slate-300'
                    }
                    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  aria-current={currentType === 'privato' ? 'page' : undefined}
                >
                  {/* Icona con effetti */}
                  <motion.div 
                    className={`
                      relative transition-all duration-300
                      ${currentType === 'privato' ? 'scale-110' : 'scale-100 group-hover:scale-105'}
                    `}
                    animate={{ 
                      rotate: currentType === 'privato' ? [0, -10, 10, 0] : 0,
                      scale: currentType === 'privato' ? 1.1 : 1
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <User 
                      size={24} 
                      className={`
                        transition-all duration-300
                        ${currentType === 'privato' 
                          ? 'text-purple-200 drop-shadow-lg' 
                          : 'text-slate-400 group-hover:text-slate-300'
                        }
                      `} 
                    />
                    {/* Check mark per stato attivo */}
                    {currentType === 'privato' && (
                      <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center animate-bounce"
                      >
                        <CheckCircle size={12} className="text-white" />
                      </motion.div>
                    )}
                  </motion.div>
                  
                  {/* Testo con animazioni */}
                  <div className="text-center">
                    <span className={`
                      font-bold text-sm transition-all duration-300
                      ${currentType === 'privato' ? 'text-white' : 'text-slate-400 group-hover:text-slate-300'}
                    `}>
                      Privato
                    </span>
                  </div>
                  
                  {/* Effetto hover */}
                  <div className={`
                    absolute inset-0 rounded-lg transition-all duration-300
                    ${currentType === 'privato' 
                      ? 'bg-gradient-to-br from-purple-500/10 to-blue-500/10' 
                      : 'group-hover:bg-slate-700/30'
                    }
                  `}></div>
                </button>
              </div>
            </div>
            
            {/* Indicatore di stato */}
            <div className="mt-4 flex items-center justify-center">
              <div className={`
                px-3 py-1 rounded-full text-xs font-medium transition-all duration-300
                ${currentType === 'azienda'
                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                  : 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                }
              `}>
                {currentType === 'azienda' ? 'Modalità Azienda' : 'Modalità Privato'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modale di conferma moderno */}
      <AnimatePresence mode="wait" initial={false}>
        {showModal && (
          <motion.div
            key="modern-form-type-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={cancelChange}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative bg-slate-800/95 backdrop-blur-md rounded-2xl p-8 max-w-md w-full border border-slate-700/50 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Icona di avviso */}
              <div className="flex justify-center mb-6">
                <div
                  className="w-16 h-16 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center"
                >
                  <AlertTriangle size={32} className="text-amber-400" />
                </div>
              </div>

              {/* Titolo */}
              <h3 className="text-xl font-bold text-white text-center mb-4">
                Cambio Modalità
              </h3>

              {/* Messaggio */}
              <p className="text-slate-300 text-center mb-8 leading-relaxed">
                {pendingType === 'privato' 
                  ? 'Passando dalla modalità Azienda alla modalità Privato, tutti i dati inseriti verranno cancellati.'
                  : 'Passando dalla modalità Privato alla modalità Azienda, tutti i dati inseriti verranno cancellati.'
                }
                <br />
                <span className="font-semibold text-amber-400">Vuoi continuare?</span>
              </p>

              {/* Pulsanti */}
              <div className="flex gap-3">
                <button
                  onClick={cancelChange}
                  className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={confirmChange}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white rounded-xl font-medium transition-all shadow-lg"
                >
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle size={16} />
                    Conferma
                  </div>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}; 